import { useState, useEffect, useCallback } from 'react';
import {
    collection,
    doc,
    setDoc,
    onSnapshot,
    Timestamp,
    serverTimestamp,
    getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Constantes
const RESERVATION_DURATION_HOURS = 48;
const RESERVATIONS_COLLECTION = 'reservations';

/**
 * Calcula la fecha de expiracion (48 horas desde ahora)
 */
const calculateExpirationDate = () => {
    const now = new Date();
    return new Date(now.getTime() + RESERVATION_DURATION_HOURS * 60 * 60 * 1000);
};

/**
 * Verifica si una reserva ha expirado
 */
const isReservationExpired = (expiresAt) => {
    if (!expiresAt) return true;
    const expirationDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
    return new Date() > expirationDate;
};

export const useReservations = () => {
    const [reservations, setReservations] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Suscribirse a cambios en tiempo real - TODOS los documentos
    useEffect(() => {
        const reservationsRef = collection(db, RESERVATIONS_COLLECTION);

        // Escuchar TODA la coleccion (sin filtro)
        // Esto permite leer tanto reservas como status (sold/hidden)
        const unsubscribe = onSnapshot(
            reservationsRef,
            (snapshot) => {
                const reservationsMap = {};

                snapshot.forEach((docSnapshot) => {
                    const data = docSnapshot.data();
                    reservationsMap[docSnapshot.id] = {
                        ...data,
                        id: docSnapshot.id
                    };
                });

                setReservations(reservationsMap);
                setLoading(false);
                setError(null);
            },
            (err) => {
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Error listening to reservations:', err);
                }
                setError(err.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    /**
     * Verifica si un producto esta reservado (48h activo)
     */
    const isProductReserved = useCallback((productId) => {
        const reservation = reservations[productId];
        if (!reservation) return false;

        // Si tiene status sold/hidden, no esta "reservado" en el sentido de 48h
        if (reservation.status === 'sold' || reservation.status === 'hidden') {
            return false;
        }

        return reservation.isReserved && !isReservationExpired(reservation.expiresAt);
    }, [reservations]);

    /**
     * Obtiene el status de un producto (sold/hidden/null)
     */
    const getProductStatus = useCallback((productId) => {
        const reservation = reservations[productId];
        return reservation?.status || null;
    }, [reservations]);

    /**
     * Obtiene informacion de reserva de un producto
     */
    const getReservationInfo = useCallback((productId) => {
        return reservations[productId] || null;
    }, [reservations]);

    /**
     * Crea una nueva reserva
     */
    const createReservation = useCallback(async (productId, customerInfo) => {
        // Verificar si ya esta reservado o vendido
        const docRef = doc(db, RESERVATIONS_COLLECTION, productId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const existingData = docSnap.data();

            // No permitir reservar si esta vendido
            if (existingData.status === 'sold') {
                throw new Error('Este producto ya ha sido vendido');
            }

            // No permitir reservar si ya esta reservado y no ha expirado
            if (existingData.isReserved && !isReservationExpired(existingData.expiresAt)) {
                throw new Error('Este producto ya esta reservado');
            }
        }

        const expirationDate = calculateExpirationDate();

        const reservationData = {
            productId,
            isReserved: true,
            status: 'available',
            reservedAt: serverTimestamp(),
            expiresAt: Timestamp.fromDate(expirationDate),
            customerInfo: {
                name: customerInfo.name || '',
                email: customerInfo.email || '',
                phone: customerInfo.phone || ''
            },
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        await setDoc(docRef, reservationData, { merge: true });

        // Actualizacion optimista del estado local
        setReservations(prev => ({
            ...prev,
            [productId]: {
                ...reservationData,
                id: productId,
                status: 'available',
                reservedAt: new Date(),
                expiresAt: { toDate: () => expirationDate }
            }
        }));

        return { success: true, expiresAt: expirationDate };
    }, []);

    /**
     * Calcula tiempo restante de una reserva
     */
    const getTimeRemaining = useCallback((productId) => {
        const reservation = reservations[productId];
        if (!reservation || !reservation.expiresAt) return null;

        // Si esta vendido, no mostrar tiempo restante
        if (reservation.status === 'sold') return null;

        const expirationDate = reservation.expiresAt.toDate
            ? reservation.expiresAt.toDate()
            : new Date(reservation.expiresAt);
        const now = new Date();
        const diff = expirationDate - now;

        if (diff <= 0) return null;

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        return { hours, minutes, totalMs: diff };
    }, [reservations]);

    return {
        reservations,
        loading,
        error,
        isProductReserved,
        getProductStatus,
        getReservationInfo,
        createReservation,
        getTimeRemaining
    };
};
